import { THREAD_MARKDOWN_COMPONENTS, ThreadMarkdownRendererContext } from './ThreadMarkdownRenderers'
import type { SourceCardImage } from '@digital-twins/shared-types'
import { ReactNode, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { KnowledgeAskCitation, KnowledgeDocumentListItem, WebCitation } from '@digital-twins/shared-types'
import { AssistantEvidenceItem } from '@digital-twins/shared-types/assistant'
import { Text } from '@/components/ui/Text'
import { ThreadAssetView } from '@/lib/api'
import { CitationSource, claimBefore, SourcePill } from '@/components/citations'
import {
  CitationHrefRef,
  citationHrefRefs,
  injectCitationMarkers,
  injectEvidenceMarkers,
} from './injectCitationMarkers'
import { createCreativeMarkdownHandlers } from './resolveCreativeMarkdown'
import { ThreadFurtherReading } from './ThreadFurtherReading'
import { useSmoothedReveal } from './useSmoothedReveal'
import { holdBackPartialAssetImage, placedAssetImageTokens } from './assetImageMarkdown'
import { RichBlock, richFenceFromPre } from './RichBlocks/RichBlock'
import { CitationSourceContext, citationSourceForLegacy, citationSourceForTarget } from './citationSources'
import { markdownLinkUrls } from './RichBlocks/RichInlineText'
import { useLinkedSocialPosts } from './useLinkedSocialPosts'
import { SocialPostEmbedCard } from '@/pages/KnowledgeHubPage/social/SocialPostEmbedCard'
import type { PartitionedEvidenceV2 } from './partitionAssistantCitations'

interface ThreadAnswerBodyProps {
  text: string
  citations?: Array<KnowledgeAskCitation>
  refused?: boolean
  streaming?: boolean
  resolveLabel: (citation: KnowledgeAskCitation) => string
  onOpenCitation: (index: number, citation: KnowledgeAskCitation) => void
  /**
   * Web citations for this turn, already split out of the mixed
   * `KnowledgeAskCitation | AssistantCitation` array by
   * `partitionAssistantCitations` — never the raw array. No longer drawn
   * under the answer: inline pills and the side pane's Sources section reach
   * every web source. Kept so call sites compile unchanged.
   */
  webCitations?: Array<WebCitation>
  /**
   * `judgment` stream event notes for this turn, in arrival order (see
   * `AssistantJudgmentEvent` in shared-types). Rendered as one quiet,
   * collapsed note after the web sources. Optional for the same reason as
   * `webCitations`.
   */
  judgmentNotes?: Array<string>
  threadAssets?: Array<ThreadAssetView>
  docsByToken?: Map<string, KnowledgeDocumentListItem>
  citationV2?: PartitionedEvidenceV2
  /** P1: a calculation chip opens the thread's sources, where its base and source live. */
  onOpenCalculation?: (evidenceId: string) => void
  /** Study and room sources open an in-app route (study results, chat room). */
  onNavigate?: (path: string) => void
  /** Offered sources the answer read but did not cite; shown once the answer settles. */
  furtherReading?: Array<AssistantEvidenceItem>
  /**
   * The `asset:` image tokens the answer on screen renders inline right now:
   * complete refs only, and only those that resolve against `threadAssets`.
   * Fires when that set changes, so the page keeps an image's tile until the
   * inline image has actually replaced it.
   */
  onPlacedAssetTokensChange?: (tokens: Array<string>) => void
  /**
   * Generated-image tiles (and their one turn-level retry). Sit after the
   * answer prose and web sources, before further reading, and show while the
   * answer is still streaming.
   */
  cardImages?: Array<SourceCardImage>
  sourceImagesLoading?: boolean
  imageTiles?: ReactNode
  /** The thread's answer language. The preparing-canvas row follows it. */
  language?: string | null
  /**
   * True while this answer is still streaming or the reveal is still writing
   * it out. The suggestion row waits on the latest answer's report.
   */
  onAnswerPendingChange?: (pending: boolean) => void
}

/** The last fairgen fence has opened and has not closed, so the cards are still being written. */
export function richFenceStillOpen(text: string): boolean {
  const open = text.lastIndexOf('```fairgen')
  if (open < 0) {
    return false
  }
  return !text.slice(open + 3).includes('```')
}

export function ThreadAnswerBody({
  text,
  citations = [],
  refused = false,
  streaming = false,
  resolveLabel,
  onOpenCitation,
  threadAssets,
  docsByToken,
  citationV2,
  onOpenCalculation,
  onNavigate,
  furtherReading,
  onPlacedAssetTokensChange,
  imageTiles,
  cardImages,
  sourceImagesLoading,
  language,
  onAnswerPendingChange,
}: ThreadAnswerBodyProps) {
  const { t } = useTranslation('app')
  const reveal = useSmoothedReveal(text, streaming)
  const answerStillShowing = Boolean(streaming) || reveal.revealing
  useEffect(() => {
    onAnswerPendingChange?.(answerStillShowing)
  }, [answerStillShowing, onAnswerPendingChange])
  // A half-written `![alt](asset:…` is held back while the answer is written
  // out, so it never flashes as raw markdown before its image renders.
  const visibleText = streaming || reveal.revealing
    ? holdBackPartialAssetImage(reveal.text)
    : reveal.text

  const placedAssetKey = useMemo(() => {
    const resolvable = new Set(
      (threadAssets ?? [])
        .filter((asset) => Boolean(asset.ref.publicUrl))
        .map((asset) => asset.token),
    )
    return placedAssetImageTokens(visibleText)
      .filter((token) => resolvable.has(token))
      .join(' ')
  }, [visibleText, threadAssets])

  const onPlacedAssetTokensChangeRef = useRef(onPlacedAssetTokensChange)
  useLayoutEffect(() => {
    onPlacedAssetTokensChangeRef.current = onPlacedAssetTokensChange
  }, [onPlacedAssetTokensChange])

  useEffect(() => {
    onPlacedAssetTokensChangeRef.current?.(placedAssetKey ? placedAssetKey.split(' ') : [])
  }, [placedAssetKey])

  // Markers are injected against the text actually on screen: citations only
  // arrive with the final frame, so during streaming this is a no-op and the
  // reveal never has to re-anchor offsets it has not reached yet.
  const placement = useMemo(
    () => citationV2
      ? streaming ? null : injectEvidenceMarkers(visibleText, citationV2)
      : injectCitationMarkers(visibleText, citations),
    [visibleText, citations, citationV2, streaming],
  )
  const markedText = placement?.text ?? visibleText

  const sourceContext = useMemo<CitationSourceContext>(
    () => ({ t, resolveLabel, docsByToken, onOpenCitation, onOpenCalculation, onNavigate }),
    [t, resolveLabel, docsByToken, onOpenCitation, onOpenCalculation, onNavigate],
  )

  // One pill per citation point: every ref the href carries, first-cited first.
  // `claim` is the answer line the point sits on: it picks the quoted passage
  // and, when no figure was named, the figure the sentence states.
  const sourceForRef = (ref: CitationHrefRef, claim: string | null): CitationSource | null => {
    if (citationV2) {
      const target = citationV2.targets[ref.index - 1]
      return target ? citationSourceForTarget(target, ref.index, sourceContext, ref.figure, claim) : null
    }
    const citation = citations[ref.index - 1]
    return citation ? citationSourceForLegacy(citation, ref.index, sourceContext, claim) : null
  }

  const creative = useMemo(
    () =>
      createCreativeMarkdownHandlers({
        assets: threadAssets,
        docsByToken,
        onOpenCitation,
      }),
    [threadAssets, docsByToken, onOpenCitation],
  )

  // A rich fence that does not parse yet renders nothing until the answer settles.
  const isAnswerPending = streaming || reveal.revealing

  // Post links in the answer, prose and rich blocks alike, resolved in one
  // lookup to the org's stored posts. Prose links show as post cards under
  // the answer; a block shows its own under each item.
  const answerLinks = useMemo(() => markdownLinkUrls(text), [text])
  const proseLinks = useMemo(() => markdownLinkUrls(text.replace(/```[\s\S]*?```/g, ' ')), [text])
  const linkedPosts = useLinkedSocialPosts(answerLinks, !isAnswerPending && !refused)
  const prosePosts = proseLinks.flatMap((url) => {
    const post = linkedPosts.get(url)
    return post ? [post] : []
  })

  if (refused) {
    return (
      <>
        <Text size="sm" className="text-chart-warning">
          {text}
        </Text>
        {imageTiles}
      </>
    )
  }

  return (
    <>
    <div
      className="max-w-prose text-base leading-[1.7] text-foreground"
      data-testid="thread-assistant-markdown"
    >
      <ThreadMarkdownRendererContext.Provider value={{
        renderPre: ({ children, node }) => {
            // `fairgen-kpis` / `fairgen-cards` fences render as native blocks (D6),
            // never as raw JSON, including while the fence is still streaming.
            const fence = richFenceFromPre(node, isAnswerPending)
            if (fence) {
              return (
                <RichBlock
                  fence={fence}
                  isPending={isAnswerPending}
                  language={language}
                  citationV2={citationV2}
                  sourceContext={sourceContext}
                  threadAssets={threadAssets}
                  linkedPosts={linkedPosts}
                  cardImages={cardImages}
                  sourceImagesLoading={sourceImagesLoading}
                />
              )
            }
            return (
              <pre
                className="my-6 overflow-x-auto rounded-md bg-muted p-4 text-[0.9em] [&_code]:bg-transparent [&_code]:p-0 [&_code]:text-inherit"
                data-testid="thread-answer-code-block"
              >
                {children}
              </pre>
            )
          },
        renderLink: ({ href, children, node }) => {
            const refs = citationHrefRefs(href)
            if (refs != null) {
              const first = refs[0].index
              const claim = claimBefore(markedText, node?.position?.start.offset)
              const sources = refs
                .map((ref) => sourceForRef(ref, claim))
                .filter((source): source is CitationSource => source != null)
              if (sources.length === 0) {
                return (
                  <sup className="text-[10px] text-muted-foreground">
                    {first}
                  </sup>
                )
              }
              return (
                <SourcePill
                  sources={sources}
                  testId={`thread-citation-marker-${first}`}
                  cardTestId={`thread-citation-tooltip-${first}`}
                  inline
                />
              )
            }
            const custom = creative.renderLink({ href, children })
            if (custom !== undefined) {
              return custom
            }
            return (
              <a
                href={href}
                className="text-link underline underline-offset-2 hover:text-link-hover"
                target="_blank"
                rel="noreferrer"
              >
                {children}
              </a>
            )
          },
        renderImage: ({ src, alt }) => {
            const custom = creative.renderImage({ src, alt })
            if (custom !== undefined) {
              return custom
            }
            return (
              <img
                src={src}
                alt={alt ?? ''}
                className="max-w-full h-auto"
              />
            )
          },
      }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        urlTransform={creative.urlTransform}
        components={THREAD_MARKDOWN_COMPONENTS}
      >
        {markedText}
      </ReactMarkdown>
      </ThreadMarkdownRendererContext.Provider>
      {reveal.revealing && !richFenceStillOpen(visibleText) ? (
        <span
          className="ml-1 inline-block h-3 w-1 animate-pulse bg-primary align-middle"
          data-testid="thread-answer-caret"
        />
      ) : null}
    </div>
    {prosePosts.length > 0 ? (
      <div className="@container flex max-w-prose flex-col gap-2 pt-3" data-testid="thread-answer-social-posts">
        {prosePosts.map((post, index) => (
          <SocialPostEmbedCard key={post.postToken} post={post} testId={`thread-answer-social-post-${index}`} />
        ))}
      </div>
    ) : null}
    {imageTiles}
    {!streaming && !reveal.revealing ? (
      <ThreadFurtherReading
        items={furtherReading}
        docsByToken={docsByToken}
        onOpenCitation={onOpenCitation}
        onNavigate={onNavigate}
      />
    ) : null}
    </>
  )
}
